import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsentStore } from '../stores/useCookieConsentStore';

export default function CookieBanner() {
  const decided = useCookieConsentStore((s) => s.decided);
  const bannerOpen = useCookieConsentStore((s) => s.bannerOpen);
  const managerOpen = useCookieConsentStore((s) => s.managerOpen);
  const choices = useCookieConsentStore((s) => s.choices);
  const ensureCurrentVersion = useCookieConsentStore((s) => s.ensureCurrentVersion);
  const acceptAll = useCookieConsentStore((s) => s.acceptAll);
  const rejectAll = useCookieConsentStore((s) => s.rejectAll);
  const saveChoices = useCookieConsentStore((s) => s.saveChoices);
  const openManager = useCookieConsentStore((s) => s.openManager);
  const closeManager = useCookieConsentStore((s) => s.closeManager);

  useEffect(() => {
    ensureCurrentVersion();
  }, [ensureCurrentVersion]);

  if (!bannerOpen && !managerOpen) return null;

  return (
    <>
      {bannerOpen && !managerOpen ? (
        <div
          role="dialog"
          aria-label="Cookie consent"
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[9998] rounded-2xl bg-white shadow-2xl border border-gray-200 p-5"
        >
          <div className="text-sm text-gray-700 leading-relaxed">
            <strong className="block text-gray-900 mb-1">We use cookies</strong>
            We use strictly necessary cookies to make the site work. With your
            permission we'd also like to use cookies for analytics and error
            tracking to improve the product.{' '}
            <Link to="/cookies" className="text-[#F47920] underline hover:text-[#d96a14]">
              Cookie Policy
            </Link>
            {' · '}
            <Link to="/privacy" className="text-[#F47920] underline hover:text-[#d96a14]">
              Privacy Policy
            </Link>
            .
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={acceptAll}
              className="flex-1 bg-[#F47920] hover:bg-[#d96a14] text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Reject all
            </button>
            <button
              type="button"
              onClick={openManager}
              className="flex-1 bg-white hover:bg-gray-50 border border-gray-300 text-gray-800 text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Manage
            </button>
          </div>
        </div>
      ) : null}

      {managerOpen ? (
        <CookiePreferencesModal
          initialChoices={choices}
          onSave={saveChoices}
          onClose={() => {
            if (decided) closeManager();
          }}
          allowClose={decided}
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
        />
      ) : null}
    </>
  );
}

function CookiePreferencesModal({ initialChoices, onSave, onClose, allowClose, onAcceptAll, onRejectAll }) {
  const [analytics, setAnalytics] = useState(Boolean(initialChoices?.analytics));
  const [errorTracking, setErrorTracking] = useState(Boolean(initialChoices?.errorTracking));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie preferences"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && allowClose) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cookie preferences</h2>
          <p className="text-sm text-gray-600 mb-4">
            Choose which categories of cookies we may use. You can change these
            preferences at any time. See our{' '}
            <Link to="/cookies" className="text-[#F47920] underline">
              Cookie Policy
            </Link>{' '}
            for full details.
          </p>

          <div className="space-y-3">
            <CategoryRow
              title="Strictly necessary"
              description="Required for login, security, and core platform functionality. Always on."
              checked
              disabled
            />

            <CategoryRow
              title="Analytics"
              description="Helps us understand which features are used and where users get stuck. Vendors: Google Tag Manager, Amplitude (anonymized events only)."
              checked={analytics}
              onChange={setAnalytics}
            />

            <CategoryRow
              title="Error tracking"
              description="Captures errors so we can fix bugs faster. Vendor: Sentry."
              checked={errorTracking}
              onChange={setErrorTracking}
            />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => onSave({ analytics, errorTracking })}
              className="flex-1 bg-[#F47920] hover:bg-[#d96a14] text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Save preferences
            </button>
            <button
              type="button"
              onClick={onAcceptAll}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={onRejectAll}
              className="flex-1 bg-white hover:bg-gray-50 border border-gray-300 text-gray-800 text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Reject all
            </button>
          </div>
          {allowClose ? (
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full text-xs text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ title, description, checked, onChange, disabled }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border ${disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-200 hover:border-gray-300 cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={Boolean(checked)}
        disabled={Boolean(disabled)}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#F47920]"
      />
      <span className="flex-1">
        <span className="block text-sm font-semibold text-gray-900">{title}</span>
        <span className="block text-xs text-gray-600 mt-0.5 leading-relaxed">{description}</span>
      </span>
    </label>
  );
}
